<template>
  <div class="order-detail-container">
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="d-flex justify-content-center align-items-center"
      style="min-height: 400px;"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
    </div>

    <b-alert
      v-else-if="error"
      variant="danger"
      show
      class="text-center"
    >
      {{ error }}
    </b-alert>

    <template v-else>
      <!-- Header Section -->
      <div class="order-header mb-4">
        <b-card class="shadow-sm border-0">
          <div class="d-flex justify-content-between align-items-start flex-wrap">
            <div class="order-title-section">
              <h2 class="order-title mb-2">
                <feather-icon
                  icon="PackageIcon"
                  class="mr-2"
                  size="28"
                />
                Colis #{{ order.id }}
              </h2>
              <div class="order-meta mb-3">
                <b-badge
                  :variant="getStatusVariant(order.status)"
                  class="status-badge mr-2"
                >
                  <feather-icon
                    :icon="getStatusIcon(order.status)"
                    size="14"
                    class="mr-1"
                  />
                  {{ getStatusLabel(order.status) }}
                </b-badge>
                <small
                  class="text-muted"
                  style="color: #4b5563 !important; font-weight: 500;"
                >
                  Code barre: {{ order.barcode }}
                </small>
                <small
                  v-if="order.external_id && (user.role === 'admin' || user.role === 'superadmin')"
                  class="text-muted ml-3"
                  style="color: #4b5563 !important; font-weight: 500;"
                >
                  ID Externe: {{ order.external_id }}
                </small>
              </div>

              <!-- Last Return Reason (only if exists) -->
              <div
                v-if="order.last_return_reason && order.last_return_reason.return_reason"
                class="mb-3"
              >
                <b-alert
                  show
                  variant="warning"
                  class="return-alert p-3"
                >
                  <div class="d-flex align-items-start">
                    <feather-icon
                      icon="AlertTriangleIcon"
                      class="mr-2 mt-1"
                      size="18"
                    />
                    <div>
                      <strong>Dernier motif de retour:</strong><br>
                      <span class="return-reason">{{ order.last_return_reason.return_reason }}</span><br>
                      <small class="text-muted">
                        {{ formatDateTime(order.last_return_reason.created_at) }}
                      </small>
                      <!-- Enhanced details for last return reason -->
                      <ul
                        class="pl-3 mb-0 mt-1"
                        style="font-size: 0.95em;"
                      >
                        <li v-if="order.last_return_reason.scheduled_date">
                          <strong>Date programmée :</strong>
                          {{ formatDateTime(order.last_return_reason.scheduled_date) }}
                        </li>
                        <li v-if="order.last_return_reason.additional_note">
                          <strong class="text-info">Remarque :</strong>
                          <span class="remarque-text">{{ order.last_return_reason.additional_note }}</span>
                        </li>
                        <li v-if="order.last_return_reason.quick_options && order.last_return_reason.quick_options.length">
                          <strong>Note :</strong>
                          <span>
                            <span
                              v-for="(opt, idx) in order.last_return_reason.quick_options"
                              :key="idx"
                            >
                              <b-badge
                                variant="secondary"
                                class="mr-1"
                              >{{ translateQuickOption(opt) }}</b-badge>
                            </span>
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </b-alert>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons d-flex flex-column align-items-end">
              <div
                v-if="(order.status == 0 || order.status == 1) && user.role === 'admin'"
                class="d-flex flex-column"
              >
                <b-button
                  variant="primary"
                  size="sm"
                  class="mb-2"
                  :to="{ name: 'order-edit', params: { id: id } }"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="16"
                    class="mr-1"
                  />
                  Modifier
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="deleteOrder(id)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="16"
                    class="mr-1"
                  />
                  Supprimer
                </b-button>
              </div>
              <b-button
                v-if="order && order.can_create_ticket && user.role === 'supplier'"
                variant="danger"
                size="sm"
                class="mb-2"
                @click="showQuickTicketModal = true"
              >
                <feather-icon
                  icon="PlusCircleIcon"
                  size="16"
                  class="mr-1"
                />
                Ticket support
              </b-button>

              <!-- Redelivery Request Button for Supplier -->
              <b-button
                v-if="showRedeliveryButton"
                variant="primary"
                size="sm"
                class="mb-2"
                @click="showRedeliveryConfirm = true"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="16"
                  class="mr-1"
                />
                Demander une réexpédition
              </b-button>
              <!-- Price Change Request Button for Supplier -->
              <b-button
                v-if="showPriceChangeButton"
                variant="success"
                size="sm"
                class="mb-2"
                @click="openPriceChangeModal"
              >
                <feather-icon
                  icon="DollarSignIcon"
                  size="16"
                  class="mr-1"
                />
                Demander une modification de prix
              </b-button>
              <b-alert
                v-if="order && order.pending_price_change_request"
                variant="info"
                show
                class="mb-2 p-2"
              >
                Une demande de modification de prix est déjà en attente de traitement.
              </b-alert>
              <!-- Confirm Dialog for Redelivery Request -->
              <b-modal
                v-model="showRedeliveryConfirm"
                title="Confirmer la demande de réexpédition"
                ok-title="Confirmer la réexpédition"
                cancel-title="Annuler"
                :ok-disabled="isLoading"
                :hide-footer="false"
                centered
                class="modern-modal"
                @ok="requestRedelivery"
              >
                <div class="d-flex align-items-start mb-2">
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="22"
                    class="mr-2 text-primary"
                  />
                  <div>
                    <b-alert
                      show
                      variant="info"
                      class="p-2 mb-0"
                    >
                      Êtes-vous sûr de vouloir demander la <b>réexpédition</b> de ce colis ?<br>
                      <span class="text-muted">Cette action notifiera notre équipe logistique et lancera une nouvelle tentative de livraison auprès du destinataire.</span>
                    </b-alert>
                  </div>
                </div>
              </b-modal>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Main Content -->
      <b-row>
        <!-- Left Column - Order Details -->
        <b-col
          cols="12"
          lg="8"
          class="mb-4"
        >
          <!-- Customer & Shipping Info -->
          <b-card class="info-card shadow-sm border-0 mb-4">
            <template #header>
              <h5 class="card-header-title mb-0">
                <feather-icon
                  icon="UserIcon"
                  class="mr-2"
                />
                Données destinataire & expédition
                <b-tooltip
                  target="order-client-info"
                  placement="top"
                >
                  Informations détaillées sur le client et l'adresse de livraison.
                </b-tooltip>
              </h5>
            </template>

            <b-row class="mt-1">
              <b-col
                cols="12"
                md="6"
              >
                <div class="info-group">
                  <h6
                    id="order-client-info"
                    class="info-label"
                  >
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                      class="mr-1"
                    />
                    Destinataire
                  </h6>
                  <p class="info-value">
                    {{ order.name }}
                  </p>
                </div>

                <div class="info-group">
                  <h6 class="info-label">
                    <feather-icon
                      icon="PhoneIcon"
                      size="16"
                      class="mr-1"
                    />
                    Téléphone principal
                  </h6>
                  <p class="info-value">
                    {{ order.phone1 }}
                    <span v-if="order.phone2"><br>{{ order.phone2 }}</span>
                  </p>
                </div>

                <div class="info-group">
                  <h6 class="info-label">
                    <feather-icon
                      icon="MapPinIcon"
                      size="16"
                      class="mr-1"
                    />
                    Adresse de livraison
                  </h6>
                  <p class="info-value">
                    {{ order.address }}
                  </p>
                </div>
              </b-col>

              <b-col
                cols="12"
                md="6"
              >
                <div class="info-group">
                  <h6 class="info-label">
                    <feather-icon
                      icon="MapIcon"
                      size="16"
                      class="mr-1"
                    />
                    Gouvernorat
                  </h6>
                  <p class="info-value">
                    {{ order.governorate.name }}
                  </p>
                </div>

                <div
                  v-if="order.delegation"
                  class="info-group"
                >
                  <h6 class="info-label">
                    <feather-icon
                      icon="MapPinIcon"
                      size="16"
                      class="mr-1"
                    />
                    Délégation
                  </h6>
                  <p class="info-value">
                    {{ order.delegation.name }}
                  </p>
                </div>

                <div
                  v-if="order.locality"
                  class="info-group"
                >
                  <h6 class="info-label">
                    <feather-icon
                      icon="Navigation2Icon"
                      size="16"
                      class="mr-1"
                    />
                    Localité
                  </h6>
                  <p class="info-value">
                    {{ order.locality.name }}
                    <small class="text-muted">({{ order.locality.zip_code }})</small>
                  </p>
                </div>
              </b-col>
            </b-row>
          </b-card>

          <!-- Order Properties -->
          <b-card class="info-card shadow-sm border-0 mb-4">
            <template #header>
              <h5 class="card-header-title mb-0">
                <feather-icon
                  icon="SettingsIcon"
                  class="mr-2"
                />
                Caractéristiques logistiques
                <b-tooltip
                  target="order-props-info"
                  placement="top"
                >
                  Détails sur la nature du colis et ses spécificités logistiques.
                </b-tooltip>
              </h5>
            </template>

            <div class="properties-grid mt-1">
              <!-- Fragile -->
              <div
                class="property-item"
                :class="{ 'active-property': order.is_fragile }"
              >
                <div class="property-icon">
                  <feather-icon
                    icon="CloudLightningIcon"
                    :class="order.is_fragile ? 'text-warning' : 'text-muted'"
                    size="24"
                  />
                </div>
                <div class="property-content">
                  <h6
                    id="order-props-info"
                    class="property-title"
                  >
                    Fragile
                  </h6>
                  <div class="mb-2">
                    <div
                      class="status-indicator"
                      :class="order.is_fragile ? 'status-yes-warning' : 'status-no'"
                    >
                      <feather-icon
                        :icon="order.is_fragile ? 'CheckIcon' : 'XIcon'"
                        size="14"
                      />
                      <span>{{ order.is_fragile ? 'OUI' : 'NON' }}</span>
                    </div>
                  </div>
                  <p class="property-description">
                    {{ getPropertyDescription('fragile', order.is_fragile) }}
                  </p>
                </div>
              </div>

              <!-- Discharge -->
              <div
                class="property-item"
                :class="{ 'active-property': order.is_discharge }"
              >
                <div class="property-icon">
                  <feather-icon
                    icon="FileTextIcon"
                    :class="order.is_discharge ? 'text-info' : 'text-muted'"
                    size="24"
                  />
                </div>
                <div class="property-content">
                  <h6 class="property-title">
                    Remise contre décharge
                  </h6>
                  <div class="mb-2">
                    <div
                      class="status-indicator"
                      :class="order.is_discharge ? 'status-yes-info' : 'status-no'"
                    >
                      <feather-icon
                        :icon="order.is_discharge ? 'CheckIcon' : 'XIcon'"
                        size="14"
                      />
                      <span>{{ order.is_discharge ? 'OUI' : 'NON' }}</span>
                    </div>
                  </div>
                  <p class="property-description">
                    {{ getPropertyDescription('discharge', order.is_discharge) }}
                  </p>
                </div>
              </div>

              <!-- Exchange -->
              <div
                class="property-item"
                :class="{ 'active-property': order.is_an_exchange }"
              >
                <div class="property-icon">
                  <feather-icon
                    icon="RefreshCwIcon"
                    :class="order.is_an_exchange ? 'text-primary' : 'text-muted'"
                    size="24"
                  />
                </div>
                <div class="property-content">
                  <h6 class="property-title">
                    Colis d'échange
                  </h6>
                  <div class="mb-2">
                    <div
                      class="status-indicator"
                      :class="order.is_an_exchange ? 'status-yes-primary' : 'status-no'"
                    >
                      <feather-icon
                        :icon="order.is_an_exchange ? 'CheckIcon' : 'XIcon'"
                        size="14"
                      />
                      <span>{{ order.is_an_exchange ? 'OUI' : 'NON' }}</span>
                    </div>
                  </div>
                  <p class="property-description">
                    {{ getPropertyDescription('exchange', order.is_an_exchange) }}
                  </p>
                </div>
              </div>

              <!-- Open Package -->
              <div
                class="property-item"
                :class="{ 'active-property': order.can_open_package }"
              >
                <div class="property-icon">
                  <feather-icon
                    icon="PackageIcon"
                    :class="order.can_open_package ? 'text-brown' : 'text-muted'"
                    :style="order.can_open_package ? 'color: #C59C6C;' : ''"
                    size="24"
                  />
                </div>
                <div class="property-content">
                  <h6 class="property-title">
                    Ouverture autorisée
                  </h6>
                  <div class="mb-2">
                    <div
                      class="status-indicator"
                      :class="order.can_open_package ? 'status-yes-brown' : 'status-no'"
                      :style="order.can_open_package ? 'background-color: #fdf8f3; color: #C59C6C; border-color: #e8dccf;' : ''"
                    >
                      <feather-icon
                        :icon="order.can_open_package ? 'CheckIcon' : 'XIcon'"
                        size="14"
                      />
                      <span>{{ order.can_open_package ? 'OUI' : 'NON' }}</span>
                    </div>
                  </div>
                  <p class="property-description">
                    {{ getPropertyDescription('open_package', order.can_open_package) }}
                  </p>
                </div>
              </div>

              <!-- Returns Count -->
              <div
                class="property-item"
                :class="{ 'active-property': order.return_number > 0 }"
              >
                <div class="property-icon">
                  <feather-icon
                    icon="RotateCcwIcon"
                    :class="order.return_number > 0 ? 'text-danger' : 'text-muted'"
                    size="24"
                  />
                </div>
                <div class="property-content">
                  <h6 class="property-title">
                    Tentatives de livraison
                  </h6>
                  <div class="mb-2">
                    <b-badge
                      :variant="getReturnNumberColor(order.return_number)"
                      class="return-count-badge"
                    >
                      {{ order.return_number }}
                    </b-badge>
                  </div>
                  <p class="property-description">
                    {{ getPropertyDescription('return_number', order.return_number) }}
                  </p>
                </div>
              </div>

              <!-- Transfer -->
              <div
                class="property-item"
                :class="{ 'active-property': order.is_transfer }"
              >
                <div class="property-icon">
                  <feather-icon
                    icon="TruckIcon"
                    :class="order.is_transfer ? 'text-secondary' : 'text-muted'"
                    size="24"
                  />
                </div>
                <div class="property-content">
                  <h6 class="property-title">
                    En transfert
                  </h6>
                  <div class="mb-2">
                    <div
                      class="status-indicator"
                      :class="order.is_transfer ? 'status-yes-secondary' : 'status-no'"
                    >
                      <feather-icon
                        :icon="order.is_transfer ? 'CheckIcon' : 'XIcon'"
                        size="14"
                      />
                      <span>{{ order.is_transfer ? 'OUI' : 'NON' }}</span>
                    </div>
                  </div>
                  <p class="property-description">
                    {{ getPropertyDescription('transfer', order.is_transfer) }}
                  </p>
                </div>
              </div>

              <!-- Payment Method -->
              <div class="property-item active-property">
                <div class="property-icon">
                  <feather-icon
                    icon="CreditCardIcon"
                    class="text-success"
                    size="24"
                  />
                </div>
                <div class="property-content">
                  <h6 class="property-title">
                    Paiement
                  </h6>
                  <div class="mb-2">
                    <b-badge :variant="order.payment_method == 0 ? 'success' : order.payment_method == 1 ? 'info' : 'primary'">
                      {{ getPaymentMethod(order.payment_method) }}
                    </b-badge>
                  </div>
                  <p class="property-description">
                    {{ getPropertyDescription('payment_method', order.payment_method) }}
                  </p>
                </div>
              </div>
            </div>
          </b-card>

          <!-- Pricing Information -->
          <b-card class="pricing-card shadow-sm border-0 mb-4">
            <template #header>
              <h5 class="card-header-title mb-0">
                <feather-icon
                  icon="DollarSignIcon"
                  class="mr-2"
                />
                Détail tarifaire
                <b-tooltip
                  target="order-pricing-info"
                  placement="top"
                >
                  Décomposition du prix du colis (HT, TVA, TTC).
                </b-tooltip>
              </h5>
            </template>

            <div class="pricing-breakdown">
              <div class="pricing-row">
                <span
                  id="order-pricing-info"
                  class="pricing-label"
                >Prix HT</span>
                <span class="pricing-value">{{ order.price_ht }} DT</span>
              </div>
              <div class="pricing-row">
                <span class="pricing-label">TVA</span>
                <span class="pricing-value">{{ order.tax }} DT</span>
              </div>
              <div class="pricing-divider" />
              <div class="pricing-row total">
                <span class="pricing-label">Prix TTC</span>
                <span class="pricing-value total-amount">{{ order.price_ttc }} DT</span>
              </div>
            </div>
          </b-card>

          <!-- Metadata -->
          <b-card class="metadata-card shadow-sm border-0">
            <template #header>
              <h5 class="card-header-title mb-0">
                <feather-icon
                  icon="InfoIcon"
                  class="mr-2"
                />
                Informations complémentaires
                <b-tooltip
                  target="order-meta-info"
                  placement="top"
                >
                  Métadonnées et informations additionnelles sur le colis.
                </b-tooltip>
              </h5>
            </template>

            <b-row class="mt-1">
              <b-col
                cols="12"
                md="6"
              >
                <div class="metadata-item">
                  <feather-icon
                    icon="HomeIcon"
                    size="16"
                    class="metadata-icon"
                  />
                  <div>
                    <small
                      id="order-meta-info"
                      class="metadata-label"
                    >Dépôt logistique</small>
                    <p class="metadata-value">
                      {{ order.repository.name }}
                    </p>
                  </div>
                </div>

                <div class="metadata-item">
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="metadata-icon"
                  />
                  <div>
                    <small class="metadata-label">Date de création</small>
                    <p class="metadata-value">
                      {{ formatDateTime(order.created_at) }}
                    </p>
                  </div>
                </div>

                <div class="metadata-item">
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="metadata-icon"
                  />
                  <div>
                    <small class="metadata-label">Arrivée au hub central</small>
                    <p class="metadata-value">
                      {{ formatDateTime(order.central_hub_received_at) }}
                    </p>
                  </div>
                </div>

                <div
                  v-if="order.weight"
                  class="metadata-item"
                >
                  <feather-icon
                    icon="PackageIcon"
                    size="16"
                    class="metadata-icon"
                  />
                  <div>
                    <small class="metadata-label">Poids volumétrique</small>
                    <p class="metadata-value">
                      {{ order.weight.weight }}
                    </p>
                  </div>
                </div>
              </b-col>

              <b-col
                cols="12"
                md="6"
              >
                <div class="metadata-item">
                  <feather-icon
                    icon="UserIcon"
                    size="16"
                    class="metadata-icon"
                  />
                  <div>
                    <small class="metadata-label">Expéditeur</small>
                    <p class="metadata-value">
                      {{ order.supplier.username }}
                    </p>
                  </div>
                </div>

                <div class="metadata-item">
                  <feather-icon
                    icon="EditIcon"
                    size="16"
                    class="metadata-icon"
                  />
                  <div>
                    <small class="metadata-label">Dernière modification</small>
                    <p class="metadata-value">
                      {{ formatDateTime(order.updated_at) }}
                    </p>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card>

          <!-- Tickets Related to This Order -->
          <b-card
            v-if="order && order.tickets && order.tickets.length"
            class="tickets-card shadow-sm border-0 mb-4"
          >
            <template #header>
              <h5 class="card-header-title mb-0">
                <feather-icon
                  icon="InfoIcon"
                  class="mr-2"
                />
                Tickets d'assistance liés à ce colis
                <b-tooltip
                  target="order-tickets-info"
                  placement="top"
                >
                  Liste des tickets de support associés à ce colis.
                </b-tooltip>
              </h5>
            </template>
            <div>
              <b-table
                :items="order.tickets"
                :fields="ticketTableFields"
                small
                responsive
                head-variant="light"
                class="tickets-table"
              >
                <template #cell(reference)="{ item }">
                  <b-link
                    id="order-tickets-info"
                    :to="{ name: 'SupportTicketDetail', params: { id: item.id } }"
                  >
                    <b-badge variant="danger">
                      #{{ item.reference }}
                    </b-badge>
                  </b-link>
                </template>
                <template #cell(category)="{ item }">
                  <span v-if="item.category && item.category.display_name">{{ item.category.display_name }}</span>
                  <span v-else>-</span>
                </template>
                <template #cell(status)="{ item }">
                  <b-badge :variant="item.status && item.status.is_closed ? 'secondary' : 'success'">
                    {{ item.status && item.status.display_name ? item.status.display_name : (item.status && item.status.name) || 'N/A' }}
                  </b-badge>
                </template>
                <template #cell(assignee)="{ item }">
                  <span v-if="showAssignee && item.assignee && item.assignee.username">{{ item.assignee.username }}</span>
                  <span
                    v-else-if="showAssignee"
                    class="text-muted"
                  >Non assigné</span>
                  <span v-else>-</span>
                </template>
                <template #cell(created_at)="{ item }">
                  {{ formatDateTime(item.created_at) }}
                </template>
                <template #cell(closed_at)="{ item }">
                  <span v-if="item.closed_at">{{ formatDateTime(item.closed_at) }}</span>
                  <span v-else>-</span>
                </template>
                <template #cell(actions)="{ item }">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="goToTicketDetail(item.id)"
                  >
                    Voir
                  </b-button>
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>

        <!-- Right Column - QR Code & Supplier -->
        <b-col
          cols="12"
          lg="4"
          class="mb-4"
        >
          <!-- QR Code Card -->
          <b-card class="qr-card shadow-sm border-0 mb-4">
            <template #header>
              <h6 class="card-header-title mb-0 text-center">
                <feather-icon
                  icon="HashIcon"
                  class="mr-1"
                />
                QR Code
              </h6>
            </template>

            <div class="qr-container">
              <qrcode-vue
                :value="order.id.toString()"
                :size="140"
                level="H"
                class="qr-code"
              />
              <p class="qr-label mt-3 mb-0 text-center">
                Colis #{{ order.id }}
              </p>
            </div>
          </b-card>

          <!-- Supplier Information -->
          <b-card
            v-if="order.supplier"
            class="supplier-card shadow-sm border-0"
          >
            <template #header>
              <h6 class="card-header-title mb-0">
                <feather-icon
                  icon="BuildingIcon"
                  class="mr-2"
                />
                Fournisseur
              </h6>
            </template>

            <div class="supplier-info mt-1">
              <div class="supplier-avatar">
                <b-avatar
                  :text="order.supplier.username.charAt(0).toUpperCase()"
                  variant="primary"
                  size="50"
                />
              </div>

              <div class="supplier-details">
                <h6 class="supplier-name">
                  {{ order.supplier.username }}
                </h6>

                <div class="supplier-field">
                  <feather-icon
                    icon="MailIcon"
                    size="14"
                    class="field-icon"
                  />
                  <span class="field-value">{{ order.supplier.email }}</span>
                </div>

                <div class="supplier-field">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="field-icon"
                  />
                  <span class="field-value">
                    {{ order.supplier.phone }}
                    <span v-if="order.supplier.phone2"> / {{ order.supplier.phone2 }}</span>
                  </span>
                </div>

                <div class="supplier-field">
                  <feather-icon
                    icon="MapPinIcon"
                    size="14"
                    class="field-icon"
                  />
                  <span class="field-value">{{ order.supplier.address }}</span>
                </div>

                <div
                  v-if="order.supplier.tax_identification_number"
                  class="supplier-field"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    size="14"
                    class="field-icon"
                  />
                  <span class="field-value">{{ order.supplier.tax_identification_number }}</span>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Bottom Section - History & Products -->
      <b-row>
        <!-- History Timeline -->
        <b-col
          cols="12"
          lg="6"
          class="mb-4"
        >
          <b-card class="history-card shadow-sm border-0">
            <template #header>
              <h5 class="card-header-title mb-0">
                <feather-icon
                  icon="ClockIcon"
                  class="mr-2"
                />
                Suivi des événements ({{ order.histories.length }})
                <b-tooltip
                  target="order-timeline-info"
                  placement="top"
                >
                  Chronologie détaillée des statuts et actions logistiques du colis.
                </b-tooltip>
              </h5>
            </template>

            <div
              id="order-timeline-info"
              class="timeline"
            >
              <div
                v-for="(item, index) in order.histories"
                :key="item.id"
                class="timeline-item"
                :class="{ 'last-item': index === order.histories.length - 1 }"
              >
                <div class="timeline-marker">
                  <div
                    class="timeline-dot"
                    :class="`dot-${getStatusColor(item.message)}`"
                  />
                </div>
                <div class="timeline-content">
                  <div class="timeline-header">
                    <h6 class="timeline-title">
                      {{ item.message }}
                    </h6>
                    <small class="timeline-time">{{ formatFn(item.created_at) }}</small>
                  </div>
                  <p class="timeline-description">
                    {{ item.description }}
                  </p>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>

        <!-- Products & Returns -->
        <b-col
          cols="12"
          lg="6"
          class="mb-4"
        >
          <!-- Products -->
          <b-card class="products-card shadow-sm border-0 mb-4">
            <template #header>
              <h5 class="card-header-title mb-0">
                <feather-icon
                  icon="ShoppingBagIcon"
                  class="mr-2"
                />
                Détail des produits ({{ items.length }})
                <b-tooltip
                  target="order-products-info"
                  placement="top"
                >
                  Liste des articles inclus dans ce colis.
                </b-tooltip>
              </h5>
            </template>

            <div
              id="order-products-info"
              class="products-list"
            >
              <div
                v-for="item in items"
                :key="item.désignation"
                class="product-item"
              >
                <div class="product-info">
                  <h6 class="product-name">
                    {{ item.désignation }}
                  </h6>
                </div>
                <div class="product-quantity">
                  <b-badge
                    variant="primary"
                    class="quantity-badge"
                  >
                    x{{ item.quantité }}
                  </b-badge>
                </div>
              </div>
            </div>
          </b-card>

          <!-- Return History -->
          <b-card
            v-if="order.return_reason_histories && order.return_reason_histories.length > 0"
            class="returns-card shadow-sm border-0"
          >
            <template #header>
              <h5 class="card-header-title mb-0">
                <feather-icon
                  icon="AlertTriangleIcon"
                  class="mr-2"
                />
                Historique des retours ({{ order.return_number }})
                <b-tooltip
                  target="order-returns-info"
                  placement="top"
                >
                  Liste chronologique des motifs de retour et actions associées.
                </b-tooltip>
              </h5>
            </template>

            <div
              id="order-returns-info"
              class="returns-list"
            >
              <div
                v-for="(returnItem, index) in order.return_reason_histories"
                :key="index"
                class="return-item"
              >
                <div class="return-content">
                  <div class="return-reason">
                    <b-badge
                      variant="warning"
                      class="reason-badge"
                    >
                      {{ returnItem.return_reason }}
                    </b-badge>
                  </div>
                  <small class="return-date">
                    {{ formatDateTime(returnItem.created_at) }}
                  </small>
                </div>
                <!-- Enhanced details -->
                <div v-if="returnItem.scheduled_date || returnItem.additional_note || (returnItem.quick_options && returnItem.quick_options.length)">
                  <ul
                    class="pl-3 mb-0"
                    style="font-size: 0.95em;"
                  >
                    <li v-if="returnItem.scheduled_date">
                      <strong>Date programmée :</strong>
                      {{ formatDateTime(returnItem.scheduled_date) }}
                    </li>
                    <li v-if="returnItem.additional_note">
                      <strong class="text-info">Remarque :</strong>
                      <span class="remarque-text">{{ returnItem.additional_note }}</span>
                    </li>
                    <li v-if="returnItem.quick_options && returnItem.quick_options.length">
                      <strong>Note :</strong>
                      <span>
                        <span
                          v-for="(opt, idx) in returnItem.quick_options"
                          :key="idx"
                        >
                          <b-badge
                            variant="secondary"
                            class="mr-1"
                          >{{ translateQuickOption(opt) }}</b-badge>
                        </span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Quick Ticket Modal -->
      <b-modal
        v-model="showQuickTicketModal"
        title="Créer un ticket support pour ce colis"
        hide-footer
        size="md"
        centered
        :no-close-on-backdrop="quickTicketLoading"
        :no-close-on-esc="quickTicketLoading"
        class="modern-modal"
      >
        <b-form @submit.prevent="submitQuickTicket">
          <b-form-group
            label="Catégorie *"
            label-for="quick-ticket-category"
          >
            <b-form-select
              id="quick-ticket-category"
              v-model="quickTicketForm.category"
              :options="quickTicketCategories.map(c => ({ value: c.id, text: c.display_name }))"
              required
              placeholder="Sélectionnez une catégorie"
            />
          </b-form-group>
          <b-form-group
            label="Description (optionnel)"
            label-for="quick-ticket-desc"
          >
            <vue-editor
              id="quick-ticket-desc"
              v-model="quickTicketForm.description"
              placeholder="Décrivez le problème... (optionnel)"
              :editor-toolbar="[
                [{ header: [1, 2, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link', 'image'],
                ['clean'],
              ]"
              style="min-height: 120px; background: #f8f9fa; border-radius: 0.75rem; border: 1.5px solid #e80000;"
            />
          </b-form-group>
          <div class="d-flex align-items-center justify-content-between">
            <b-button
              type="submit"
              variant="primary"
              class="modern-btn"
              :disabled="quickTicketLoading"
            >
              <b-spinner
                v-if="quickTicketLoading"
                small
                class="mr-1"
              />
              Créer le ticket
            </b-button>
            <b-button
              variant="outline-secondary"
              :disabled="quickTicketLoading"
              @click="showQuickTicketModal = false"
            >
              Annuler
            </b-button>
          </div>
          <div
            v-if="quickTicketError"
            class="text-danger mt-2"
          >
            {{ quickTicketError }}
          </div>
          <div
            v-if="quickTicketSuccess"
            class="text-success mt-2"
          >
            Ticket créé avec succès !
          </div>
        </b-form>
      </b-modal>

      <!-- Redelivery Confirm Modal (removed duplicate, only enhanced modal remains) -->

      <!-- Price Change Request Modal -->
      <b-modal
        v-model="showPriceChangeModal"
        title="Demande de modification de prix"
        hide-footer
        size="md"
        centered
        :no-close-on-backdrop="priceChangeLoading"
        :no-close-on-esc="priceChangeLoading"
        class="modern-modal"
      >
        <b-form @submit.prevent="submitPriceChangeRequest">
          <b-form-group
            label="Nouveau prix TTC *"
            label-for="price-change-new-price"
          >
            <b-form-input
              id="price-change-new-price"
              v-model="priceChangeForm.new_price"
              type="number"
              min="0"
              step="0.001"
              required
              placeholder="Nouveau prix TTC"
            />
          </b-form-group>
          <b-form-group
            label="Motif (optionnel)"
            label-for="price-change-reason"
          >
            <b-form-textarea
              id="price-change-reason"
              v-model="priceChangeForm.reason"
              placeholder="Expliquez la raison de la modification (optionnel)"
              rows="3"
            />
          </b-form-group>
          <div class="d-flex align-items-center justify-content-between">
            <b-button
              type="submit"
              variant="success"
              :disabled="priceChangeLoading"
            >
              <b-spinner
                v-if="priceChangeLoading"
                small
                class="mr-1"
              />
              Envoyer la demande
            </b-button>
            <b-button
              variant="outline-secondary"
              :disabled="priceChangeLoading"
              @click="showPriceChangeModal = false"
            >
              Annuler
            </b-button>
          </div>
          <div
            v-if="priceChangeError"
            class="text-danger mt-2"
          >
            {{ priceChangeError }}
          </div>
          <div
            v-if="priceChangeSuccess"
            class="text-success mt-2"
          >
            Demande envoyée avec succès !
          </div>
        </b-form>
      </b-modal>
    </template>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BAvatar,
  BRow,
  BCol,
  BBadge,
  BAlert,
  BSpinner,
  BLink,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
} from 'bootstrap-vue'
import { VueEditor } from 'vue2-editor'

import storeAuth from '@/store/store'
import axios from 'axios'
import Ripple from 'vue-ripple-directive'
import QrcodeVue from 'qrcode.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ShowOrder',
  directives: {
    Ripple,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BAvatar,
    BBadge,
    BAlert,
    BSpinner,
    QrcodeVue,
    VueEditor,
    BLink,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
  },
  data() {
    return {
      order: null,
      fields: ['désignation', 'quantité'],
      returnFields: [
        { key: 'return_reason', label: 'Motif de retour' },
        { key: 'created_at', label: 'Date' },
      ],
      items: [],
      user: {},
      id: '',
      isLoading: false,
      error: null,
      ticketTableFields: [
        { key: 'reference', label: 'Référence' },
        { key: 'category', label: 'Catégorie' },
        { key: 'status', label: 'Statut' },
        { key: 'created_at', label: 'Créé le' },
        { key: 'closed_at', label: 'Clôturé le' },
        { key: 'actions', label: 'Actions' },
      ],
      showQuickTicketModal: false,
      quickTicketForm: {
        category: null, // Added
        description: '',
      },
      quickTicketCategories: [],
      quickTicketLoading: false,
      quickTicketError: '',
      quickTicketSuccess: false,
      showRedeliveryConfirm: false, // For confirm dialog
      showPriceChangeModal: false,
      priceChangeForm: {
        new_price: '',
        reason: '',
      },
      priceChangeLoading: false,
      priceChangeError: '',
      priceChangeSuccess: false,
    }
  },
  computed: {
    // Show button only for supplier and allowed statuses (strict check)
    showRedeliveryButton() {
      if (!this.order || !this.user) return false
      // Only supplier can see, and only for RETOUR_AU_FOURNISSEUR (7) or RETOUR_PAYE (9)
      const allowedStatuses = [7, 9]
      // For RETOUR_PAYE, must be paid
      if (
        this.user.role === 'supplier'
        && allowedStatuses.includes(this.order.status)
        && !this.order.pending_redelivery_request
      ) {
        if (this.order.status === 9 && !this.order.is_payed) {
          return false
        }
        return true
      }
      return false
    },
    // New supplier-only button for allowed statuses
    showSupplierSpecialButton() {
      if (!this.order || !this.user) return false
      const allowedStatuses = [0, 1, 2, 3, 5, 7, 8, 10, 11, 12, 13, 14, 15, 16, 17, 18]
      return (
        this.user.role === 'supplier'
        && allowedStatuses.includes(this.order.status)
      )
    },
    // Show price change request button for supplier, only if no pending request
    showPriceChangeButton() {
      if (!this.order || !this.user) return false
      const allowedStatuses = [0, 1, 2, 3, 5, 7, 8, 10, 11, 12, 13, 14, 15, 16, 17, 18]
      return (
        this.user.role === 'supplier'
        && allowedStatuses.includes(this.order.status)
        && !this.order.pending_price_change_request
      )
    },
    showAssignee() {
      const privilegedRoles = ['admin', 'manager', 'warehouse_operator', 'customer_support']
      return this.user && this.user.role && privilegedRoles.includes(this.user.role)
    },
  },
  async created() {
    this.user = storeAuth.state.user
    this.id = this.$route.params.id
    this.isLoading = true
    this.error = null
    try {
      const response = await axios.get(`/api/orders/detail/${this.$route.params.id}`)
      this.order = response.data
      if (this.order && this.order.products) {
        this.items = []
        this.order.products.forEach(product => {
          this.addnewitem(product.quantity, product.product)
        })
      }
      // Fetch ticket categories for quick ticket modal
      await this.fetchQuickTicketCategories()
    } catch (error) {
      this.error = error.response?.data?.message || 'Erreur lors du chargement du colis'
    } finally {
      this.isLoading = false
    }
  },
  beforeRouteEnter(to, from, next) {
    // Update breadcrumb with order ID when entering the route
    next(vm => {
      if (to.params.id) {
        // Create new breadcrumb array to avoid mutating route meta directly
        const newBreadcrumb = [
          {
            text: 'Colis',
            to: { name: 'order-list', params: { level: 0 } },
          },
          {
            text: `Détails Colis #${to.params.id}`,
            active: true,
          },
        ]

        // Use Vue.set to properly update the meta without direct assignment
        vm.$set(vm.$route.meta, 'breadcrumb', newBreadcrumb)
      }
    })
  },
  methods: {
    async requestRedelivery() {
      try {
        this.isLoading = true
        await axios.post('/api/support/orderredeliveryrequests/', {
          order: this.order.id,
        })
        this.showToast('success', 'top-center', 'Demande de réexpédition envoyée avec succès.')
        // Refresh order data to update UI (hide button if request is now pending)
        await this.refreshOrderData()
      } catch (e) {
        this.showToast('danger', 'top-center', e.response?.data?.detail || 'Erreur lors de la demande de réexpédition.')
      } finally {
        this.isLoading = false
      }
    },

    async refreshOrderData() {
      try {
        const response = await axios.get(`/api/orders/detail/${this.order.id}`)
        this.order = response.data
        // Optionally, update items if products changed
        if (this.order && this.order.products) {
          this.items = []
          this.order.products.forEach(product => {
            this.addnewitem(product.quantity, product.product)
          })
        }
      } catch (e) {
        // Optionally handle error
      }
    },
    getReturnNumberColor(returnNumber) {
      if (returnNumber === 0) {
        return 'secondary' // Gray color for 0
      } if (returnNumber === 1) {
        return 'info' // Blue color for 1
      } if (returnNumber === 2) {
        return 'warning' // Yellow color for 2
      } if (returnNumber >= 3) {
        return 'danger' // Red color for 3 or more
      }
      return 'secondary' // Default color
    },

    getStatusVariant(status) {
      const variants = {
        0: 'warning',
        1: 'info',
        2: 'primary',
        3: 'secondary',
        4: 'success',
        5: 'danger',
        6: 'success',
        7: 'danger',
        8: 'info',
        9: 'danger',
        10: 'danger',
        11: 'danger',
        12: 'danger',
        13: 'danger',
        14: 'warning',
        15: 'info',
        16: 'warning',
        17: 'success',
        18: 'info',
        19: 'success',
        20: 'warning',
      }
      return variants[status] || 'secondary'
    },

    getStatusIcon(status) {
      const icons = {
        0: 'ClockIcon',
        1: 'TruckIcon',
        2: 'HomeIcon',
        3: 'NavigationIcon',
        4: 'CheckCircleIcon',
        5: 'XCircleIcon',
        6: 'CheckCircle2Icon',
        7: 'CornerUpLeftIcon',
        8: 'ArrowRightIcon',
        9: 'CornerUpLeftIcon',
        10: 'ArrowLeftIcon',
        11: 'ArrowDownIcon',
        12: 'XIcon',
        13: 'AlertTriangleIcon',
        14: 'AlertCircleIcon',
        15: 'ClockIcon',
        16: 'DollarSignIcon',
        17: 'CheckIcon',
        18: 'CalendarIcon',
        19: 'CheckIcon',
        20: 'ClockIcon',
      }
      return icons[status] || 'HelpCircleIcon'
    },

    getStatusLabel(status) {
      const labels = {
        0: 'En attente',
        1: 'En pickup',
        2: 'Au dépôt',
        3: 'En cours',
        4: 'Livré',
        5: 'Retour au dépôt',
        6: 'Livré payé',
        7: 'Retour au fournisseur',
        8: 'Transfert',
        9: 'Retour payé',
        10: 'Retour inter-agence',
        11: 'Retour inter-agence reçu',
        12: 'Colis annulé par admin',
        13: 'Colis perdu',
        14: 'À vérifier',
        15: 'En attente de collecte',
        16: 'A rembourser',
        17: 'Remboursé',
        18: 'Rendez Vous',
        19: 'Livré Temporaire',
        20: 'Retour Temporaire',
      }
      return labels[status] || 'Statut inconnu'
    },

    getStatusColor(status) {
      const colors = {
        'En attente': 'warning',
        'En pickup': 'info',
        'En attente de collecte': 'info',
        'Au dépôt': 'primary',
        'En cours': 'secondary',
        Livré: 'success',
        'Retour au dépôt': 'danger',
        'Retour au fournisseur': 'danger',
        'Colis annulé par admin': 'danger',
        'En transfert': 'info',
        Transfert: 'info',
        'À vérifier': 'warning',
      }
      return colors[status] || 'secondary'
    },

    addnewitem(qte, nom) {
      this.items.push({
        désignation: nom,
        quantité: qte,
      })
    },

    formatFn(date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },

    formatDateTime(dateString) {
      if (!dateString) return '-'

      try {
        const date = new Date(dateString)
        return date.toLocaleString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (error) {
        return dateString
      }
    },

    async deleteOrder(id) {
      try {
        const result = await this.$swal({
          title: 'Vous êtes sûr?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, supprimer!',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })
        if (result.value) {
          try {
            await axios.delete(`/api/orders/delete/${id}/`)
            this.$router.push('/orders/0')
            this.showToast('success', 'top-center', 'Colis supprimé avec succès')
          } catch (error) {
            this.showToast('danger', 'top-center', 'Erreur lors de la suppression')
          }
        }
      } catch (e) {
        // handle error if needed
      }
    },

    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },

    translateQuickOption(opt) {
      // Translate backend quick option codes to French
      const mapping = {
        no_answer: 'Pas de réponse',
        wrong_address: 'Adresse incorrecte',
        reschedule: 'Reprogrammer',
        holiday: 'Jour férié',
        closed_business: 'Entreprise fermée',
        wrong_hours: 'Heures de fermeture',
        payment_refused: 'Refus de paiement',
        product_refused: 'Refus du produit',
        change_mind: "Changement d'avis",
        morning_delivery: 'Livraison matinée',
        afternoon_delivery: 'Livraison après-midi',
        weekend_delivery: 'Livraison weekend',
        specific_time: 'Heure précise',
        call_before: 'Appeler avant',
      }
      return mapping[opt] || opt
    },

    getPaymentMethod(method) {
      const methods = {
        0: 'Espèces',
        1: 'Chèque',
        2: 'Traite',
        cash: 'Espèces',
        card: 'Carte',
        bank_transfer: 'Virement',
        check: 'Chèque',
        cod: 'Paiement à la livraison',
        online: 'Paiement en ligne',
      }
      return methods[method] || 'Non spécifié'
    },

    goToTicketDetail(ticketId) {
      this.$router.push({ name: 'SupportTicketDetail', params: { id: ticketId } })
    },

    async fetchQuickTicketCategories() {
      try {
        const { data } = await axios.get('/api/support/categories/')
        this.quickTicketCategories = data
      } catch (e) {
        this.quickTicketCategories = []
      }
    },

    async submitQuickTicket() {
      this.quickTicketLoading = true
      this.quickTicketError = ''
      try {
        if (!this.quickTicketForm.category) {
          this.quickTicketError = 'La catégorie est requise.'
          this.quickTicketLoading = false
          return
        }
        await axios.post('/api/support/tickets/', {
          category: this.quickTicketForm.category,
          description: this.quickTicketForm.description,
          order_ids: [this.order.id],
        })
        this.quickTicketSuccess = true
        setTimeout(() => {
          this.showQuickTicketModal = false
          this.quickTicketForm = { category: null, description: '' }
          this.quickTicketSuccess = false
        }, 1200)
      } catch (e) {
        this.quickTicketError = e.response?.data?.detail || 'Erreur lors de la création du ticket.'
      } finally {
        this.quickTicketLoading = false
      }
    },

    // New supplier special action handler
    onSupplierSpecialAction() {
      this.showToast('info', 'top-center', 'Action spéciale fournisseur exécutée !')
      // TODO: Implement the real action here
    },

    openPriceChangeModal() {
      this.priceChangeForm = {
        new_price: this.order.price_ttc || '',
        reason: '',
      }
      this.priceChangeError = ''
      this.priceChangeSuccess = false
      this.showPriceChangeModal = true
    },
    async submitPriceChangeRequest() {
      this.priceChangeLoading = true
      this.priceChangeError = ''
      this.priceChangeSuccess = false
      if (!this.priceChangeForm.new_price) {
        this.priceChangeError = 'Le nouveau prix est requis.'
        this.priceChangeLoading = false
        return
      }
      try {
        await axios.post('/api/support/pricechangerequests/', {
          order_id: this.order.id,
          new_price: this.priceChangeForm.new_price,
          old_price: this.order.price_ttc,
          reason: this.priceChangeForm.reason,
        })
        this.priceChangeSuccess = true
        this.showToast('success', 'top-center', 'Demande de modification de prix envoyée.')
        // Refresh order data to update UI (hide button if request is now pending)
        await this.refreshOrderData()
        setTimeout(() => {
          this.showPriceChangeModal = false
          this.priceChangeSuccess = false
        }, 1200)
      } catch (e) {
        this.priceChangeError = e.response?.data?.detail || 'Erreur lors de la demande.'
      } finally {
        this.priceChangeLoading = false
      }
    },

    getPropertyDescription(type, value) {
      const descriptions = {
        fragile: {
          true: 'Contenu fragile. À manipuler avec soin.',
          false: 'Pas de précaution particulière.',
        },
        discharge: {
          true: 'Signature du destinataire requise.',
          false: 'Signature non obligatoire.',
        },
        exchange: {
          true: "Récupération d'un article à la livraison.",
          false: 'Livraison simple sans échange.',
        },
        open_package: {
          true: 'Ouverture autorisée avant paiement.',
          false: 'Ouverture interdite avant paiement.',
        },
        transfer: {
          true: 'Colis en transit entre dépôts.',
          false: 'Circuit de livraison standard.',
        },
      }

      if (type === 'return_number') {
        return value > 0
          ? `${value} tentative(s) de livraison échouée(s).`
          : 'Aucun retour enregistré pour ce colis.'
      }

      if (type === 'payment_method') {
        const methods = {
          0: 'Paiement en espèces à la livraison.',
          1: 'Paiement par chèque à la livraison.',
          2: 'Paiement par traite bancaire.',
          cash: 'Paiement en espèces à la livraison.',
          card: 'Paiement par carte bancaire.',
          check: 'Paiement par chèque à la livraison.',
          cod: 'Paiement à la livraison.',
          online: 'Paiement déjà effectué en ligne.',
        }
        return methods[value] || 'Mode de paiement non spécifié.'
      }

      if (descriptions[type]) {
        return descriptions[type][value ? 'true' : 'false']
      }
      return ''
    },
  },
}
</script>

<style scoped>
.order-detail-container {
  padding: 1rem;
}

/* Header Styles */
.order-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 0;
  margin-bottom: 2rem;
}

.order-header .card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
}

.order-title {
  color: #1f2937;
  font-weight: 700;
  margin: 0;
}

.order-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.status-badge {
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 500;
}

.return-alert {
  border-radius: 8px;
  border-left: 4px solid #f39c12;
}

.return-reason {
  font-weight: 500;
  color: #856404;
}

/* Card Styles */
.info-card,
.pricing-card,
.metadata-card,
.qr-card,
.supplier-card,
.history-card,
.products-card,
.returns-card,
.tickets-card {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.info-card:hover,
.pricing-card:hover,
.metadata-card:hover,
.qr-card:hover,
.supplier-card:hover,
.history-card:hover,
.products-card:hover,
.returns-card:hover,
.tickets-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.card-header-title {
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
}

/* Info Groups */
.info-group {
  margin-bottom: 1.5rem;
}

.info-label {
  color: #4b5563; /* Darker gray for better contrast */
  font-size: 0.875rem;
  font-weight: 600; /* Increased weight */
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.info-value {
  color: #111827; /* Almost black */
  font-weight: 500;
  margin: 0;
  word-break: break-word;
}

/* Properties Grid */
.properties-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.property-item {
  display: flex;
  align-items: flex-start; /* Align to top for better multi-line text */
  padding: 1.25rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.3s ease;
  height: 100%; /* Ensure equal height in grid */
}

.property-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.property-icon {
  margin-right: 1rem;
  flex-shrink: 0;
}

.property-title {
  color: #374151; /* Dark gray */
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.property-value {
  margin: 0;
}

.return-count-badge {
  font-size: 1rem;
  padding: 0.5rem 0.75rem;
}

.property-description {
  font-size: 0.85rem;
  color: #4b5563; /* Readable gray */
  margin-top: 0.75rem;
  line-height: 1.5;
}

.active-property {
  background: #f9fafb; /* Very subtle gray background */
  border-color: #6366f1; /* Indigo border accent */
  border-left-width: 4px;
}

/* Status Indicators */
.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.85rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-no {
  background-color: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}

.status-yes-warning {
  background-color: #fffbeb;
  color: #d97706;
  border: 1px solid #fcd34d;
}

.status-yes-info {
  background-color: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.status-yes-primary {
  background-color: #f5f3ff;
  color: #7c3aed;
  border: 1px solid #ddd6fe;
}

.status-yes-secondary {
  background-color: #f3f4f6;
  color: #4b5563;
  border: 1px solid #d1d5db;
}

/* Pricing Styles */
.pricing-breakdown {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
}

.pricing-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
}

.pricing-row.total {
  border-top: 2px solid #dee2e6;
  margin-top: 0.5rem;
  padding-top: 1rem;
}

.pricing-label {
  color: #4b5563;
  font-weight: 600;
}

.pricing-value {
  color: #111827;
  font-weight: 700;
}

.total-amount {
  font-size: 1.25rem;
  color: #28a745;
}

.pricing-divider {
  height: 1px;
  background: #dee2e6;
  margin: 0.5rem 0;
}

/* Metadata Styles */
.metadata-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.metadata-icon {
  margin-right: 0.75rem;
  margin-top: 0.25rem;
  color: #6c757d;
  flex-shrink: 0;
}

.metadata-label {
  color: #4b5563;
  font-weight: 600;
}

.metadata-value {
  color: #2c3e50;
  margin: 0;
  font-weight: 500;
}

/* QR Code Styles */
.qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
}

.qr-code {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.qr-label {
  color: #6c757d;
  font-weight: 500;
  font-size: 0.875rem;
}

/* Supplier Styles */
.supplier-info {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.supplier-avatar {
  flex-shrink: 0;
}

.supplier-details {
  flex: 1;
}

.supplier-name {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 1rem;
}

.supplier-field {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}

.field-icon {
  margin-right: 0.5rem;
  color: #6c757d;
  flex-shrink: 0;
}

.field-value {
  color: #495057;
  font-size: 0.875rem;
  word-break: break-word;
}

/* Timeline Styles */
.timeline {
  position: relative;
  padding-left: 2rem;
}

.timeline-item {
  position: relative;
  margin-bottom: 2rem;
}

.timeline-item:not(.last-item)::before {
  content: '';
  position: absolute;
  left: -1.5rem;
  top: 2rem;
  width: 2px;
  height: calc(100% + 1rem);
  background: #e9ecef;
}

.timeline-marker {
  position: absolute;
  left: -2rem;
  top: 0.5rem;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dot-success { background: #28a745; }
.dot-warning { background: #ffc107; }
.dot-danger { background: #dc3545; }
.dot-info { background: #17a2b8; }
.dot-primary { background: #007bff; }
.dot-secondary { background: #6c757d; }

.timeline-content {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.timeline-title {
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
  font-size: 0.95rem;
}

.timeline-time {
  color: #6c757d;
  font-size: 0.8rem;
  white-space: nowrap;
}

.timeline-description {
  color: #495057;
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.4;
}

/* Products Styles */
.products-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.product-item:hover {
  background: #e9ecef;
}

.product-name {
  color: #2c3e50;
  margin: 0;
  font-weight: 500;
  font-size: 0.95rem;
}

.quantity-badge {
  font-weight: 600;
  padding: 0.5rem 0.75rem;
}

/* Returns Styles */
.returns-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.return-item {
  padding: 1rem;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
}

.return-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.reason-badge {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  white-space: normal;
  word-break: break-word;
}

.return-date {
  color: #6c757d;
  white-space: nowrap;
  font-size: 0.8rem;
}

.remarque-text {
  color: #0d6efd;
  font-weight: 500;
  display: block;
  margin-top: 0.25em;
  margin-bottom: 0.25em;
  white-space: pre-line;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 120px;
}

/* Tickets Styles */
.tickets-card {
  border-radius: 12px;
  margin-bottom: 2rem;
}

.tickets-table {
  font-size: 0.97rem;
}

/* Modal Styles */
.modern-modal .modal-content {
  border-radius: 0.75rem;
  overflow: hidden;
}

.modern-modal .modal-header {
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.modern-modal .modal-title {
  font-weight: 500;
  color: #2c3e50;
}

.modern-modal .modal-body {
  padding: 1.5rem;
}

.modern-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 1.5rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .order-detail-container {
    padding: 0.5rem;
  }

  .order-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .properties-grid {
    grid-template-columns: 1fr;
  }

  .timeline {
    padding-left: 1.5rem;
  }

  .timeline-marker {
    left: -1.5rem;
  }

  .timeline-item:not(.last-item)::before {
    left: -1rem;
  }

  .supplier-info {
    flex-direction: column;
    text-align: center;
  }

  .return-content {
    flex-direction: column;
    gap: 0.5rem;
  }

  .action-buttons {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
  }
}

@media (max-width: 576px) {
  .order-title {
    font-size: 1.5rem;
  }

  .card-header-title {
    font-size: 1rem;
  }

  .timeline-header {
    flex-direction: column;
    gap: 0.25rem;
  }

  .timeline-time {
    align-self: flex-start;
  }
}
</style>
